import React, { useEffect, useState } from "react";
import api, { humanizeAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserSwitch, Plus, ArrowClockwise } from "@phosphor-icons/react";

const ROLE_LABEL = {
  admin: "Admin",
  hr: "İK",
  gm: "Genel Müdür",
  manager: "Müdür",
  staff: "Personel",
  accounting: "Muhasebe",
};

export default function Delegations() {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [allLog, setAllLog] = useState([]);
  const [cands, setCands] = useState({ grantable_roles: [], candidates: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ delegate_id: "", granted_role: "", reason: "", expires_at: "", use_delegator_signature: false });
  const canSeeAll = ["admin", "hr", "gm"].includes(user?.role);

  const load = async () => {
    try {
      const [m, c] = await Promise.all([
        api.get("/delegations?scope=mine"),
        api.get("/delegations/candidates"),
      ]);
      setMine(m.data);
      setCands(c.data);
      if (canSeeAll) {
        const a = await api.get("/delegations?scope=all");
        setAllLog(a.data);
      }
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.delegate_id || !form.granted_role || !form.reason) return toast.error("Delege, rol ve gerekçe zorunlu");
    try {
      await api.post("/delegations", {
        delegate_id: form.delegate_id,
        granted_role: form.granted_role,
        reason: form.reason,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        use_delegator_signature: form.use_delegator_signature,
      });
      toast.success("Yetki devredildi");
      setDialogOpen(false);
      setForm({ delegate_id: "", granted_role: "", reason: "", expires_at: "", use_delegator_signature: false });
      load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const revoke = async (d) => {
    if (!window.confirm(`${d.delegate_name} için yetki devrini iptal etmek istiyor musunuz?`)) return;
    try { await api.post(`/delegations/${d.id}/revoke`); toast.success("İptal edildi"); load(); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const canDelegate = cands.grantable_roles.length > 0 && cands.candidates.length > 0;
  const iGave = mine.filter((d) => d.delegator_id === user?.id);
  const iGot = mine.filter((d) => d.delegate_id === user?.id);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Güvenlik</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-2"><UserSwitch size={28} /> Yetki Devri</h1>
        </div>
        {canDelegate && (
          <Button onClick={() => setDialogOpen(true)} data-testid="new-delegation">
            <Plus size={14} className="mr-1" /> Yetki Devret
          </Button>
        )}
      </div>

      <Tabs defaultValue="mine">
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-mine">Bana Ait ({mine.length})</TabsTrigger>
          {canSeeAll && <TabsTrigger value="log" data-testid="tab-log">Tüm Devir Kayıtları ({allLog.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="mine">
          <div className="grid md:grid-cols-2 gap-4">
            <Section title="Verdiğim Yetkiler" rows={iGave} showRevoke onRevoke={revoke} currentUser={user} />
            <Section title="Aldığım Yetkiler" rows={iGot} showRevoke={false} currentUser={user} />
          </div>
        </TabsContent>

        {canSeeAll && (
          <TabsContent value="log">
            <Section title="Tüm Yetki Devri Kayıtları" rows={allLog} showRevoke={false} adminMode currentUser={user} onRevoke={revoke} />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Yetki Devri</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Devredilecek Rol</Label>
              <Select value={form.granted_role} onValueChange={(v) => setForm({...form, granted_role: v})}>
                <SelectTrigger data-testid="deleg-role"><SelectValue placeholder="Rol seçin" /></SelectTrigger>
                <SelectContent>
                  {cands.grantable_roles.map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hedef Kullanıcı</Label>
              <Select value={form.delegate_id} onValueChange={(v) => setForm({...form, delegate_id: v})}>
                <SelectTrigger data-testid="deleg-target"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                <SelectContent>
                  {cands.candidates.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.display_name} — {c.position || ROLE_LABEL[c.role]} ({c.department || "-"})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bitiş Tarihi (opsiyonel)</Label>
              <Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})} data-testid="deleg-expires" />
              <div className="text-xs text-muted-foreground mt-1">Boş = manuel iptal edilene kadar</div>
            </div>
            <div>
              <Label>Gerekçe</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} rows={3} data-testid="deleg-reason" />
            </div>
            <label className="flex items-start gap-2 rounded-md border border-border p-3 cursor-pointer" data-testid="deleg-use-my-sig-wrap">
              <input type="checkbox" className="mt-1" checked={form.use_delegator_signature} onChange={(e) => setForm({...form, use_delegator_signature: e.target.checked})} data-testid="deleg-use-my-sig" />
              <span className="text-sm">
                <span className="font-medium">Onaylarken benim imzam kullanılsın (vekaleten)</span>
                <span className="block text-xs text-muted-foreground mt-0.5">İşaretlenirse, bu kişi izin onaylarken sizin imzanız görünür; formda "… tarafından vekaleten imzalandı" notu eklenir.</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Vazgeç</Button>
            <Button onClick={submit} data-testid="deleg-submit">Devret</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, rows, showRevoke, adminMode, currentUser, onRevoke }) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="p-3 text-xs uppercase tracking-widest text-muted-foreground border-b border-border font-semibold">{title}</div>
        <div className="divide-y divide-border">
          {rows.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Kayıt yok</div>}
          {rows.map((d) => (
            <div key={d.id} className="p-3 text-sm space-y-1" data-testid={`deleg-row-${d.id}`}>
              <div className="flex justify-between items-start gap-2 flex-wrap">
                <div className="font-semibold">
                  {d.delegator_name} <span className="text-muted-foreground">→</span> {d.delegate_name}
                </div>
                <Badge variant={d.active ? "default" : "secondary"}>
                  {d.active ? "Aktif" : "Pasif"} · {ROLE_LABEL[d.granted_role] || d.granted_role}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                Başlangıç: {d.created_at?.slice(0, 16).replace("T", " ")}
                {d.expires_at && <> · Bitiş: {d.expires_at.slice(0, 16).replace("T", " ")}</>}
                {d.revoked_at && <> · İptal: {d.revoked_at.slice(0, 16).replace("T", " ")}</>}
              </div>
              <div className="text-xs italic">"{d.reason}"</div>
              {d.active && (showRevoke || (adminMode && ["admin", "gm"].includes(currentUser?.role))) && (
                <div>
                  <Button size="sm" variant="ghost" className="text-destructive h-7 px-2 text-xs" onClick={() => onRevoke(d)} data-testid={`revoke-${d.id}`}>
                    <ArrowClockwise size={12} className="mr-1" /> Yetkiyi Geri Al
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
